import { useEffect, useState } from "react";
import { Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { CHANNELS } from "../../lib/api";
import { STATUS } from "../../lib/api";
import MessageEditor from "./MessageEditor";
import { TRIGGERS, WAIT_UNITS, blockDef, EMAIL_TEMPLATES, WA_TEMPLATES } from "./blocks";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const WA_SAMPLE = {
  nome: "Mario", customer_name: "Mario Rossi", ristorante: "Trattoria da Mario",
  data: "25/08", ora: "20:00", coupon_name: "Sconto 20%", codice: "DH-AB12CD",
  data_inizio: "01/06", data_fine: "31/12",
  web_form_url: "https://app.dishenda.it/p/…/m/prenota",
  coupon_url: "https://app.dishenda.it/p/…/c/sconto20",
  coupon_pdf_url: "https://app.dishenda.it/api/public/coupon-pdf/…",
  menu_url: "https://app.dishenda.it/p/…/menu",
};
const OPERATORS = [">=", "<=", "=", ">", "<"];
const CHANNEL_OF = { invia_whatsapp: "whatsapp", invia_email: "email", invia_sms: "sms", invia_push: "push" };

export default function ConfigPanel({ node, onChange, onDelete, onClose, currentAutoId, linkedCouponId }) {
  const [coupons, setCoupons] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [webForms, setWebForms] = useState([]);
  const [creatingTpl, setCreatingTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplCat, setTplCat] = useState("MARKETING");
  const [tplBtnText, setTplBtnText] = useState("");
  const [tplBtnUrl, setTplBtnUrl] = useState("");

  useEffect(() => {
    if (!node) return;
    const kind = node.data.kind;
    if (kind === "assegna_coupon" || kind === "coupon_prenotato" || kind === "coupon_scaricato") api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    if (kind === "avvia_automazione") api.get("/automations").then((r) => setAutomations(r.data.filter((a) => a.id !== currentAutoId))).catch(() => {});
    if (kind === "invia_sondaggio") api.get("/surveys").then((r) => setSurveys(r.data)).catch(() => {});
    if (kind === "invia_whatsapp") {
      api.get("/integrations/whatsapp/templates/approved").then((r) => setWaTemplates(r.data)).catch(() => {});
      api.get("/web-forms").then((r) => setWebForms((r.data || []).filter((f) => f.active))).catch(() => {});
    }
  }, [node, currentAutoId]);

  // per l'anteprima col nome reale del coupon collegato serve la lista anche sui nodi azione
  useEffect(() => {
    if (linkedCouponId && !coupons.length) api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  }, [linkedCouponId]);

  if (!node) return null;
  const { kind, category } = node.data;
  const cfg = node.data.config || {};
  const def = blockDef(category, kind);
  const setCfg = (patch) => onChange(node.id, { ...cfg, ...patch });
  const setLabel = (label) => onChange(node.id, cfg, label);
  // anteprima: usa il nome reale del coupon collegato al trigger, non il campione generico
  const linkedCoupon = coupons.find((c) => c.id === linkedCouponId);
  const SAMPLE = { ...WA_SAMPLE, coupon_name: linkedCoupon?.name || WA_SAMPLE.coupon_name };

  const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
  const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";

  const createTplFromText = async () => {
    const text = (cfg.body || "").trim();
    if (!text) { toast.error("Scrivi prima il testo del messaggio"); return; }
    const seen = [];
    const metaBody = text.replace(/\{([a-z_]+)\}/gi, (m, v) => {
      const k = v.toLowerCase();
      if (!seen.includes(k)) seen.push(k);
      return `{{${seen.indexOf(k) + 1}}}`;
    });
    const examples = seen.map((v) => ({ nome: "Mario", cognome: "Rossi", data: "25/08", ora: "20:00" })[v] || "esempio");
    const name = (tplName || "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!name) { toast.error("Dai un nome al template (minuscole, numeri e _)"); return; }
    setCreatingTpl(true);
    try {
      const btnUrl = tplBtnUrl.trim();
      const buttons = btnUrl
        ? [{ type: "URL", text: tplBtnText.trim() || "Clicca qui e Prenota", url: btnUrl }]
        : [];
      const r = await api.post("/integrations/whatsapp/templates",
        { name, category: tplCat, language: "it", body: metaBody, examples, buttons });
      toast.success(r.data.status === "BOZZA"
        ? `Template "${name}" salvato come bozza (collega WhatsApp Business per inviarlo a Meta)`
        : `Template "${name}" inviato a Meta: apparirà nel menu a tendina quando approvato`);
    } catch (e) { toast.error(apiError(e)); }
    setCreatingTpl(false);
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-zinc-200" data-testid="config-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">{def?.label || kind}</div>
          <input value={node.data.label} onChange={(e) => setLabel(e.target.value)} data-testid="cfg-label"
            className="font-display font-bold text-zinc-900 text-sm w-full bg-transparent focus:outline-none border-b border-transparent focus:border-zinc-300" />
        </div>
        <div className="flex gap-1">
          {category !== "trigger" && (
            <button onClick={() => onDelete(node.id)} data-testid="cfg-delete"
              className="p-2 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition"><Trash size={16} /></button>
          )}
          <button onClick={onClose} className="p-2 rounded-md text-zinc-400 hover:text-zinc-900 md:hidden"><X size={16} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {kind === "cliente_inattivo" && (
          <div>
            <label className={labelCls}>Giorni di inattività</label>
            <input type="number" min="1" value={cfg.days ?? 30} onChange={(e) => setCfg({ days: parseInt(e.target.value) || 30 })}
              className={inputCls} data-testid="cfg-days" />
          </div>
        )}

        {kind === "tempo_arrivo" && category === "trigger" && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Ore</label>
              <input type="number" min="1" value={cfg.hours ?? 3} onChange={(e) => setCfg({ hours: parseInt(e.target.value) || 3 })}
                className={inputCls} data-testid="cfg-tempo-ore" />
            </div>
            <div>
              <label className={labelCls}>Quando</label>
              <select value={cfg.direction || "prima"} onChange={(e) => setCfg({ direction: e.target.value })}
                className={inputCls} data-testid="cfg-tempo-direzione">
                <option value="prima">prima dell'arrivo</option>
                <option value="dopo">dopo l'arrivo</option>
              </select>
            </div>
          </div>
        )}

        {kind === "coupon_scaricato" && category === "trigger" && (
          <div>
            <label className={labelCls}>Coupon collegato</label>
            <select value={cfg.coupon_id || ""} onChange={(e) => setCfg({ coupon_id: e.target.value || null })}
              className={inputCls} data-testid="cfg-trigger-coupon">
              <option value="">Qualsiasi coupon</option>
              {coupons.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <p className="text-[10px] text-zinc-400 mt-1">Il flusso scatta solo per il download di questo coupon. Nel messaggio, {"{coupon_name}"} e {"{codice}"} mostrano nome e codice del coupon effettivamente scaricato.</p>
          </div>
        )}

        {kind === "tag_aggiunto" && category === "trigger" && (
          <div>
            <label className={labelCls}>Tag (vuoto = qualsiasi tag)</label>
            <input value={cfg.tag || ""} onChange={(e) => setCfg({ tag: e.target.value })}
              placeholder="es. VIP" className={inputCls} data-testid="cfg-tag-trigger" />
          </div>
        )}

        {category === "wait" && (
          <>
            <div>
              <label className={labelCls}>Tipo di attesa</label>
              <select value={cfg.unit || "giorni"} onChange={(e) => setCfg({ unit: e.target.value })} className={inputCls} data-testid="cfg-wait-unit">
                {WAIT_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
            {!["data", "evento"].includes(cfg.unit) && (
              <div>
                <label className={labelCls}>Durata ({cfg.unit})</label>
                <input type="number" min="1" value={cfg.value ?? 1} onChange={(e) => setCfg({ value: parseInt(e.target.value) || 1 })}
                  className={inputCls} data-testid="cfg-wait-value" />
              </div>
            )}
            {cfg.unit === "data" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Data</label>
                  <input type="date" value={cfg.date || ""} onChange={(e) => setCfg({ date: e.target.value })} className={inputCls} data-testid="cfg-wait-date" />
                </div>
                <div>
                  <label className={labelCls}>Ora</label>
                  <input type="time" value={cfg.time || "09:00"} onChange={(e) => setCfg({ time: e.target.value })} className={inputCls} data-testid="cfg-wait-time" />
                </div>
              </div>
            )}
            {cfg.unit === "evento" && (
              <div>
                <label className={labelCls}>Attendi evento</label>
                <select value={cfg.event || ""} onChange={(e) => setCfg({ event: e.target.value })} className={inputCls} data-testid="cfg-wait-event">
                  <option value="">Seleziona evento...</option>
                  {TRIGGERS.map((t) => <option key={t.kind} value={t.kind}>{t.label}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        {category === "condition" && (
          <>
            {["numero_visite", "spesa_totale", "voto_sondaggio"].includes(kind) && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Operatore</label>
                  <select value={cfg.operator || ">="} onChange={(e) => setCfg({ operator: e.target.value })} className={inputCls} data-testid="cfg-operator">
                    {OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Valore</label>
                  <input type="number" value={cfg.value ?? 0} onChange={(e) => setCfg({ value: e.target.value })} className={inputCls} data-testid="cfg-value" />
                </div>
              </div>
            )}
            {kind === "consenso_marketing" && (
              <select value={cfg.value || "si"} onChange={(e) => setCfg({ value: e.target.value })} className={inputCls} data-testid="cfg-consent">
                <option value="si">Ha dato il consenso</option>
                <option value="no">Non ha dato il consenso</option>
              </select>
            )}
            {kind === "lingua" && (
              <div>
                <label className={labelCls}>Lingua (codice)</label>
                <input value={cfg.value || "it"} onChange={(e) => setCfg({ value: e.target.value })} className={inputCls} data-testid="cfg-lang" />
              </div>
            )}
            {kind === "tag" && (
              <div>
                <label className={labelCls}>Tag</label>
                <input value={cfg.value || ""} onChange={(e) => setCfg({ value: e.target.value })} className={inputCls} data-testid="cfg-tag" />
              </div>
            )}
            {kind === "provenienza" && (
              <div>
                <label className={labelCls}>Canale</label>
                <select value={cfg.value || ""} onChange={(e) => setCfg({ value: e.target.value })} className={inputCls} data-testid="cfg-channel">
                  {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {kind === "giorno" && (
              <div>
                <label className={labelCls}>Giorni</label>
                <div className="flex flex-wrap gap-1">
                  {WEEKDAYS.map((d, i) => {
                    const active = (cfg.days || []).includes(i);
                    return (
                      <button type="button" key={d} data-testid={`cfg-day-${i}`}
                        onClick={() => setCfg({ days: active ? (cfg.days || []).filter((x) => x !== i) : [...(cfg.days || []), i] })}
                        className={`h-9 px-3 rounded-md border text-xs font-semibold transition ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {kind === "fascia_oraria" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Dalle</label>
                  <input type="time" value={cfg.from || "18:00"} onChange={(e) => setCfg({ from: e.target.value })} className={inputCls} data-testid="cfg-from" />
                </div>
                <div>
                  <label className={labelCls}>Alle</label>
                  <input type="time" value={cfg.to || "23:00"} onChange={(e) => setCfg({ to: e.target.value })} className={inputCls} data-testid="cfg-to" />
                </div>
              </div>
            )}
            {kind === "coupon_prenotato" && (
              <div>
                <label className={labelCls}>Coupon da controllare</label>
                <select value={cfg.coupon_id || ""} onChange={(e) => setCfg({ coupon_id: e.target.value })} className={inputCls} data-testid="cfg-coupon-prenotato">
                  <option value="">Il coupon di questa automazione (consigliato)</option>
                  {coupons.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-[11px] text-zinc-400 mt-1">SÌ se il cliente ha una prenotazione agganciata a quel coupon (fatta dal modulo web) o lo ha già utilizzato. Vuoto = il coupon scaricato in questa automazione.</p>
              </div>
            )}
            {!["numero_visite", "spesa_totale", "consenso_marketing", "lingua", "tag", "provenienza", "giorno", "fascia_oraria", "coupon_prenotato"].includes(kind) && (
              <p className="text-xs text-zinc-500">Nessuna configurazione necessaria. Collega i rami SÌ e NO sul canvas.</p>
            )}
            <p className="text-xs text-zinc-400">Uscita verde = SÌ · uscita rossa = NO</p>
          </>
        )}

        {category === "action" && (
          <>
            {kind === "invia_email" && (
              <div>
                <label className={labelCls}>Tipo di email</label>
                <select value={cfg.email_template || "standard"} data-testid="cfg-email-template"
                  onChange={(e) => {
                    const t = EMAIL_TEMPLATES.find((x) => x.k === e.target.value);
                    setCfg({ email_template: e.target.value, ...(t ? t.defaults : {}) });
                  }}
                  className={inputCls}>
                  {EMAIL_TEMPLATES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
                </select>
                <p className="text-[11px] text-zinc-400 mt-1">Il modello precompila oggetto, testo e pulsante: puoi modificarli qui sotto prima di salvare.</p>
              </div>
            )}
            {CHANNEL_OF[kind] && (
              <MessageEditor value={cfg} onChange={(v) => onChange(node.id, v)} channel={CHANNEL_OF[kind]}
                linkedCouponName={linkedCoupon?.name} />
            )}
            {kind === "invia_whatsapp" && (
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Tipo di messaggio</label>
                  <select value={cfg.wa_preset || "standard"} data-testid="cfg-wa-preset"
                    onChange={(e) => {
                      const t = WA_TEMPLATES.find((x) => x.k === e.target.value);
                      setCfg({ wa_preset: e.target.value, ...(t && t.k !== "standard" ? t.defaults : {}) });
                    }}
                    className={inputCls}>
                    {WA_TEMPLATES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
                  </select>
                  {cfg.wa_preset === "sondaggio" && (
                    <p className="text-[11px] text-zinc-400 mt-1">Per inviare il link tracciato del sondaggio usa il blocco "Invia sondaggio": crea l'invito con token automaticamente.</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Template WhatsApp (solo approvati Meta — per invii fuori finestra 24h)</label>
                  <select value={cfg.wa_template || ""} data-testid="cfg-wa-template"
                    onChange={(e) => setCfg({ wa_template: e.target.value, wa_params: [] })}
                    className={inputCls}>
                    <option value="">Testo libero (solo finestra 24h)</option>
                    {waTemplates.map((t) => (
                      <option key={t.id} value={t.name}>{t.name} · {t.language}</option>
                    ))}
                  </select>
                  {cfg.wa_template && waTemplates.length === 0 && (
                    <p className="text-[11px] text-amber-700">Nessun template approvato: crealo in Integrazioni → Template messaggi.</p>
                  )}
                </div>
                {cfg.wa_template && (() => {
                  const tpl = waTemplates.find((t) => t.name === cfg.wa_template);
                  const bodyComp = (tpl?.components || []).find((c) => c.type === "BODY");
                  const nVars = (bodyComp?.text?.match(/\{\{\d+\}\}/g) || []).length;
                  return nVars > 0 ? (
                    <div className="space-y-1.5" data-testid="cfg-wa-params">
                      <label className={labelCls}>Valori variabili (accetta variabili flusso come {"{nome}"})</label>
                      {Array.from({ length: nVars }).map((_, i) => (
                        <input key={i} value={(cfg.wa_params || [])[i] || ""} placeholder={`{{${i + 1}}} = …`}
                          onChange={(e) => {
                            const p = [...(cfg.wa_params || [])];
                            p[i] = e.target.value;
                            setCfg({ wa_params: p });
                          }}
                          className={inputCls} data-testid={`cfg-wa-param-${i}`} />
                      ))}
                    </div>
                  ) : null;
                })()}
                {!cfg.wa_template && (cfg.body || "").trim() && (
                  <div className="border border-emerald-200 bg-emerald-50/60 rounded-md p-2.5 space-y-2" data-testid="cfg-wa-newtpl">
                    <p className="text-[11px] text-zinc-600">Fuori dalla finestra 24h Meta accetta solo template approvati. Trasforma questo testo in template: le variabili come {"{nome}"} diventano {"{{1}}"}, {"{{2}}"}…</p>
                    <div className="flex gap-1.5">
                      <input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="nome_template"
                        className={inputCls} data-testid="cfg-wa-newtpl-name" />
                      <select value={tplCat} onChange={(e) => setTplCat(e.target.value)} className={inputCls} data-testid="cfg-wa-newtpl-cat">
                        <option value="MARKETING">Marketing</option>
                        <option value="UTILITY">Utility</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <input value={tplBtnText} onChange={(e) => setTplBtnText(e.target.value)} placeholder="Testo pulsante (opz.)"
                        className={inputCls} data-testid="cfg-wa-newtpl-btn-text" />
                      <input value={tplBtnUrl} onChange={(e) => setTplBtnUrl(e.target.value)} placeholder="https:// link pulsante (opz.)"
                        className={inputCls} data-testid="cfg-wa-newtpl-btn-url" />
                    </div>
                    <button onClick={createTplFromText} disabled={creatingTpl} data-testid="cfg-wa-create-tpl"
                      className="w-full h-9 rounded-md bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50 transition">
                      {creatingTpl ? "Invio a Meta..." : "Crea template da questo testo"}
                    </button>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Modulo prenotazione (link {"{web_form_url}"})</label>
                  <select value={cfg.web_form_id || ""} onChange={(e) => setCfg({ web_form_id: e.target.value })}
                    className={inputCls} data-testid="cfg-wa-webform">
                    <option value="">Predefinito (da coupon o pagina ristorante)</option>
                    {webForms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Errore di consegna</label>
                    <select value={cfg.on_error || "niente"} onChange={(e) => setCfg({ on_error: e.target.value })}
                      className={inputCls} data-testid="cfg-wa-onerror">
                      <option value="niente">Non fare nulla</option>
                      <option value="email">Invia email di fallback</option>
                      <option value="notifica_staff">Notifica lo staff</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Modalità di invio</label>
                    <select value={cfg.send_mode || "tutti"} onChange={(e) => setCfg({ send_mode: e.target.value })}
                      className={inputCls} data-testid="cfg-wa-sendmode">
                      <option value="tutti">Invia a tutti i contatti</option>
                      <option value="consenso">Solo con consenso marketing</option>
                    </select>
                  </div>
                </div>
                {cfg.wa_template && (() => {
                  const tpl = waTemplates.find((t) => t.name === cfg.wa_template);
                  const btns = ((tpl?.components || []).find((c) => c.type === "BUTTONS")?.buttons) || [];
                  const dyn = btns.filter((b) => b.type === "URL" && /\{\{\d+\}\}/.test(b.url || ""));
                  return dyn.length > 0 ? (
                    <div className="space-y-1.5" data-testid="cfg-wa-btn-params">
                      <label className={labelCls}>Link del pulsante — parte finale (accetta variabili come {"{codice}"})</label>
                      {dyn.map((b, k) => (
                        <div key={k}>
                          <div className="text-[11px] text-zinc-500 mb-0.5 font-mono">{(b.url || "").replace(/\{\{\d+\}\}/, "▸ …")}</div>
                          <input value={(cfg.wa_button_params || [])[k] || ""} placeholder="vuoto = codice coupon (es. DH-1A2B3C)"
                            onChange={(e) => {
                              const p = [...(cfg.wa_button_params || [])];
                              p[k] = e.target.value;
                              setCfg({ wa_button_params: p });
                            }}
                            className={inputCls} data-testid={`cfg-wa-btn-param-${k}`} />
                        </div>
                      ))}
                      <p className="text-[11px] text-zinc-500">L'inizio del link è fissato nel template approvato da Meta e non si può cambiare; qui configuri solo la parte finale.</p>
                    </div>
                  ) : null;
                })()}
                {cfg.wa_template && (() => {
                  const tpl = waTemplates.find((t) => t.name === cfg.wa_template);
                  if (!tpl) return null;
                  const comp = (type) => (tpl.components || []).find((c) => c.type === type);
                  const headerC = comp("HEADER");
                  const bodyC = comp("BODY");
                  const footerC = comp("FOOTER");
                  const buttonsC = comp("BUTTONS");
                  const fill = (txt) => {
                    // esempi forniti a Meta in fase di approvazione (body_text), come fallback
                    const examples = (bodyC?.example?.body_text?.[0]) || [];
                    return (txt || "").replace(/\{\{(\d+)\}\}/g, (m, n) => {
                      const idx = parseInt(n, 10) - 1;
                      const raw = (cfg.wa_params || [])[idx];
                      if (!raw) return examples[idx] || m;
                      return String(raw).replace(/\{(\w+)\}/g, (mm, v) => SAMPLE[v] ?? mm);
                    });
                  };
                  return (
                    <div data-testid="cfg-wa-tpl-preview">
                      <label className={labelCls}>Anteprima template — verrà inviato questo, non il testo libero</label>
                      <div className="rounded-lg rounded-tr-none bg-[#e7f6e9] border border-emerald-200 p-3 shadow-sm space-y-1.5">
                        {headerC?.format === "TEXT" && headerC.text && (
                          <div className="text-[13px] font-bold text-zinc-900">{fill(headerC.text)}</div>
                        )}
                        <div className="text-[13px] leading-snug text-zinc-800 whitespace-pre-line">{fill(bodyC?.text)}</div>
                        {footerC?.text && <div className="text-[11px] text-zinc-500">{footerC.text}</div>}
                        {(buttonsC?.buttons || []).length > 0 && (
                          <div className="space-y-1 pt-1">
                            {buttonsC.buttons.map((b, i) => (
                              <div key={i} className="border-t border-emerald-300/70 pt-1.5 text-center text-[13px] font-medium text-sky-700">
                                {b.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {(buttonsC?.buttons || []).some((b) => b.type === "URL" && /\{\{\d+\}\}/.test(b.url || "")) && (
                        <p className="text-[11px] text-amber-700 mt-1">Il pulsante ha un link dinamico: come suffisso verrà usato il codice coupon del flusso (o le variabili che imposti).</p>
                      )}
                    </div>
                  );
                })()}
                {!cfg.wa_template && (cfg.body || "").trim() && (
                  <div data-testid="cfg-wa-preview">
                    <label className={labelCls}>Anteprima testo libero</label>
                    <div className="rounded-lg rounded-tr-none bg-[#e7f6e9] border border-emerald-200 p-3 text-[13px] leading-snug text-zinc-800 whitespace-pre-line shadow-sm">
                      {(cfg.body || "").replace(/\{(\w+)\}/g, (m, v) => SAMPLE[v] ?? m)}
                    </div>
                  </div>
                )}
              </div>
            )}
            {kind === "crea_coupon" && (
              <>
                <div><label className={labelCls}>Nome coupon</label>
                  <input value={cfg.name || ""} onChange={(e) => setCfg({ name: e.target.value })} className={inputCls} data-testid="cfg-coupon-name" /></div>
                <div><label className={labelCls}>Descrizione</label>
                  <input value={cfg.description || ""} onChange={(e) => setCfg({ description: e.target.value })} className={inputCls} data-testid="cfg-coupon-desc" /></div>
                <div><label className={labelCls}>Validità (giorni)</label>
                  <input type="number" min="1" value={cfg.valid_days ?? 30} onChange={(e) => setCfg({ valid_days: parseInt(e.target.value) || 30 })} className={inputCls} data-testid="cfg-coupon-days" /></div>
                <div><label className={labelCls}>Condizioni</label>
                  <input value={cfg.conditions || ""} onChange={(e) => setCfg({ conditions: e.target.value })} className={inputCls} data-testid="cfg-coupon-conditions" /></div>
              </>
            )}
            {kind === "assegna_coupon" && (
              <div>
                <label className={labelCls}>Coupon da assegnare (vuoto = quello appena creato)</label>
                <select value={cfg.coupon_id || ""} onChange={(e) => setCfg({ coupon_id: e.target.value })} className={inputCls} data-testid="cfg-assign-coupon">
                  <option value="">Ultimo coupon creato nel flusso</option>
                  {coupons.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {kind === "invia_sondaggio" && (
              <>
                <div>
                  <label className={labelCls}>Sondaggio da inviare</label>
                  <select value={cfg.survey_id || ""} onChange={(e) => setCfg({ survey_id: e.target.value })} className={inputCls} data-testid="cfg-survey">
                    <option value="">Seleziona sondaggio...</option>
                    {surveys.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Canale</label>
                  <select value={cfg.channel || "whatsapp"} onChange={(e) => setCfg({ channel: e.target.value })} className={inputCls} data-testid="cfg-survey-channel">
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>
                <div><label className={labelCls}>Messaggio di accompagnamento (variabili {"{nome}"}, {"{ristorante}"}...)</label>
                  <textarea rows={3} value={cfg.body || ""} onChange={(e) => setCfg({ body: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="cfg-survey-body" /></div>
              </>
            )}
            {kind === "aggiorna_stato_prenotazione" && (
              <div>
                <label className={labelCls}>Nuovo stato</label>
                <select value={cfg.status || "confermata"} onChange={(e) => setCfg({ status: e.target.value })} className={inputCls} data-testid="cfg-status">
                  {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                </select>
              </div>
            )}
            {kind === "aggiorna_cliente" && (
              <>
                <div>
                  <label className={labelCls}>Campo</label>
                  <select value={cfg.field || "notes"} onChange={(e) => setCfg({ field: e.target.value })} className={inputCls} data-testid="cfg-field">
                    <option value="notes">Note</option>
                    <option value="language">Lingua</option>
                    <option value="marketing_consent">Consenso marketing (si/no)</option>
                    <option value="city">Città</option>
                    <option value="email">Email</option>
                  </select>
                </div>
                <div><label className={labelCls}>Valore</label>
                  <input value={cfg.value ?? ""} onChange={(e) => setCfg({ value: e.target.value })} className={inputCls} data-testid="cfg-field-value" /></div>
              </>
            )}
            {["assegna_tag", "rimuovi_tag"].includes(kind) && (
              <div><label className={labelCls}>Tag</label>
                <input value={cfg.tag || ""} onChange={(e) => setCfg({ tag: e.target.value })} className={inputCls} data-testid="cfg-tag-action" /></div>
            )}
            {kind === "crea_attivita" && (
              <div><label className={labelCls}>Testo attività</label>
                <textarea rows={3} value={cfg.text || ""} onChange={(e) => setCfg({ text: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="cfg-activity" /></div>
            )}
            {kind === "notifica_staff" && (
              <div><label className={labelCls}>Messaggio allo staff</label>
                <textarea rows={3} value={cfg.message || ""} onChange={(e) => setCfg({ message: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="cfg-notify" /></div>
            )}
            {kind === "avvia_automazione" && (
              <div>
                <label className={labelCls}>Automazione da avviare</label>
                <select value={cfg.automation_id || ""} onChange={(e) => setCfg({ automation_id: e.target.value })} className={inputCls} data-testid="cfg-auto">
                  <option value="">Seleziona...</option>
                  {automations.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            {kind === "webhook" && (
              <div><label className={labelCls}>URL webhook (POST)</label>
                <input value={cfg.url || ""} onChange={(e) => setCfg({ url: e.target.value })} placeholder="https://..."
                  className={inputCls} data-testid="cfg-webhook" /></div>
            )}
            {kind === "fine" && <p className="text-xs text-zinc-500">Termina il flusso per questo cliente.</p>}
          </>
        )}

        {category === "trigger" && kind !== "cliente_inattivo" && (
          <p className="text-xs text-zinc-500">Il flusso parte quando si verifica questo evento.</p>
        )}
      </div>
    </div>
  );
}
